import { useEffect, useState } from 'react'
import jwtDecode from 'jwt-decode'
import { useFirebolt } from '@iq-firebolt/client'
import useModalController from 'hooks/useModalController'
import scrollTo from 'utils/scrollTo'
import getQueryParams from 'utils/getQueryParams'

import useAnalytics from 'hooks/useAnalytics'
import { useGetApplication } from './useGetApplication'
import { getCookie } from 'utils/cookie'

export default function useFormFlow({ alternativeLoaderConfig } = {}) {
  const [showAlternativeLoader, setShowAlternativeLoader] = useState(false)

  const {
    formflowMetadata,
    currentStep,
    goPreviousStep,
    capturedData,
    formEndPayload,
    lastVisitedStep,
    remoteErrors,
    ...props
  } = useFirebolt()

  const { track, dispatchedEvents, setDispatchedEvents } = useAnalytics() || {}

  const customerName = capturedData?.full_name
  const stepsList = formflowMetadata?.steps
  const fireboltTrackSlug = capturedData?.track
  const sendProposalModalName = 'sending-proposal-modal'

  const { openedModals, openModal, closeModal } = useModalController()
  const { isAc, application } = useGetApplication()

  useEffect(() => {
    let fireboltStore = getFireboltStore()

    const { firebolt_auth } = getQueryParams()
    // TODO: make this dynamic
    const partner = 'bb'
    // END TODO
    const haveFireboltAuth = firebolt_auth !== '' && firebolt_auth !== undefined
    const haveFireboltStore =
      fireboltStore !== null && fireboltStore?.sessionKeys

    if (haveFireboltAuth) {
      if (haveFireboltStore) {
        fireboltStore.sessionKeys[partner] = firebolt_auth
      } else {
        fireboltStore = { sessionKeys: { [partner]: firebolt_auth } }
      }

      localStorage.setItem('firebolt-store', JSON.stringify(fireboltStore))
    }
  }, [])

  useEffect(() => {
    if (currentStep?.position === 1) {
      handleFormViewedEvent()
    }
  }, [currentStep])

  useEffect(() => {
    // handle form_started event
    setTimeout(() => {
      const form = document.querySelector('form[data-fblt="firebolt-form"]')
      const formFocusEvent = () => {
        track('formStarted', {
          ...currentStep?.data,
          currentStep,
          capturedData,
        })
        form.removeEventListener('change', formFocusEvent)
      }
      if (form && currentStep?.position === 1) {
        form.addEventListener('change', formFocusEvent)
      }
    }, 100)
  }, [currentStep])

  useEffect(() => {
    if (remoteErrors.length > 0) {
      const [error] = remoteErrors
      const [result] = error?.validationResults
      handleFormErroredEvent(result)
    }
  }, [remoteErrors])

  const getFireboltStore = () =>
    localStorage ? JSON.parse(localStorage.getItem('firebolt-store')) : null

  useEffect(() => {
    const fireboltStore = getFireboltStore()

    if (fireboltStore) {
      const { partner } = capturedData
      const partnersToExpire = ['bb']

      if (partnersToExpire.includes(partner)) {
        const partnerData = fireboltStore?.sessionKeys[partner] || null
        const today = new Date()

        if (partnerData) {
          const decodedData = jwtDecode(partnerData) || {}
          const iat =
            decodedData?.iat || new Date(today.valueOf() - 1000 * 3600 * 24)
          const creationDate = new Date(iat * 1000)
          const maxDate = new Date(creationDate.valueOf() + 1000 * 3600 * 24)

          if (today > maxDate) {
            localStorage.removeItem('firebolt-store')
            localStorage.removeItem('firebolt-customStore')
            location.reload()
          }
        }
      }
    }
  }, [getFireboltStore])

  function handleFormViewedEvent() {
    const { data } = currentStep
    const isFormReady = data.sharedata
      ? data.sharedata && !props.isFormLoading
      : !props.isFormLoading
    const hasntDispatched = !dispatchedEvents.includes('formViewed')

    if (isFormReady && data.slug && hasntDispatched) {
      setDispatchedEvents('formViewed')
      track('formViewed', { ...data, capturedData, ...currentStep })
    }
  }

  function handleFormErroredEvent(error) {
    const { data } = currentStep
    const hasntDispatched = !dispatchedEvents.includes('formErrored')

    if (data.slug && hasntDispatched) {
      setDispatchedEvents('formErrored')
      track('formErrored', { ...data, ...currentStep, error, capturedData })
    }
  }

  function onBeforeProceedHandler() {
    if (alternativeLoaderConfig?.listOfSteps) handleAlternativeLoader()
  }

  function onBeforeChangeStepHandler(
    proceed,
    { leavingStep, stepPayload, enteringStep }
  ) {
    const safeStepPayload = stepPayload || {}
    const isNext = enteringStep.position > leavingStep.position
    const isFirstStep = currentStep?.position === 1

    if (isNext) {
      track('formContinued', {
        ...safeStepPayload,
        ...leavingStep?.data,
        capturedData,
        formEndPayload,
        fields: leavingStep?.data?.fields,
        currentStep: leavingStep,
      })
    }

    if (isFirstStep && isNext) {
      const isBB = capturedData?.choosen_card === 'bb-ourocard'
      const finalSlug = isBB ? 'cards-bancodobrasil-ourocardfacil' : capturedData?.product_slug
      const initialSlug = getCookie('initial_slug') || finalSlug
      const category = isBB ? 'cards' : capturedData?.product.category
      const fireboltId = capturedData?.id
      const marketplace = isAc ? 'ac' : application
      const redirectUrl =
        window.location.origin +
        window.location.pathname +
        `?passo=${enteringStep.data.slug}`

      track('proposalStarted', {
        initialSlug,
        finalSlug,
        category,
        redirectUrl,
        fireboltId,
        marketplace,
      })
    }

    proceed()
  }

  function onChageStepHandler({ sentStep, currentStep }) {
    scrollTo(0, 450)

    const fromStep = sentStep?.position
    const toStep = currentStep?.position
    const isProceed = fromStep < toStep

    if (!isProceed) {
      track('formBacked', { ...currentStep, capturedData })
    }
  }

  function onFireboltConnectionError() {
    console.log('connection error')
  }

  function onGoPreviousStep() {
    const isFirstStep = currentStep?.position === 1

    if (isFirstStep) {
      const fullPathname = window.location.pathname
      const newPathName = fullPathname.split('/proposta')[0]
      window.location.pathname = newPathName
    } else {
      if (alternativeLoaderConfig?.skipCondition?.useOnGoBack) {
        handleAlternativeLoader(true)
      }
      goPreviousStep()
    }
  }

  function openSendFormModal() {
    openModal(sendProposalModalName)
  }

  function closeSendFormModal() {
    closeModal(sendProposalModalName)
  }

  function handleAlternativeLoader(goBack = false) {
    const { listOfSteps, skipCondition } = alternativeLoaderConfig
    const isAlternativeLoaderStep = listOfSteps.includes(currentStep.data.slug)

    if (!isAlternativeLoaderStep) {
      const currentStepIndex = stepsList.findIndex(
        (x) => x.position === currentStep.position
      )

      const nextStepIndex =
        currentStepIndex === stepsList.length + 1
          ? currentStepIndex
          : currentStepIndex + 1
      const previousStepIndex =
        currentStepIndex === 0 ? currentStepIndex : currentStepIndex - 1

      const nextStepSlug = stepsList[nextStepIndex]?.slug
      const previousStepSlug = stepsList[previousStepIndex]?.slug

      const { urlParamName, urlParamValue, stepsToSkip } = skipCondition
      const hasValidSkipParam =
        new URLSearchParams(window.location.href).get(urlParamName) ===
        urlParamValue
      const skipSatisfied =
        stepsToSkip.includes(nextStepSlug) && hasValidSkipParam
      const checkPreviousStep = goBack && listOfSteps.includes(previousStepSlug)

      const useAlternativeLoader =
        listOfSteps.includes(nextStepSlug) || skipSatisfied || checkPreviousStep

      return setShowAlternativeLoader(useAlternativeLoader)
    } else {
      return setShowAlternativeLoader(false)
    }
  }

  return {
    fireboltTrackSlug,
    currentStep,
    stepsList,
    customerName,
    onChageStepHandler,
    onFireboltConnectionError,
    sendProposalModalName,
    openedModals,
    openSendFormModal,
    closeSendFormModal,
    onGoPreviousStep,
    onBeforeChangeStepHandler,
    onBeforeProceedHandler,
    showAlternativeLoader,
  }
}
